import { useEffect, useState } from "react";
import { useLocation, Outlet, Link } from "react-router-dom";
import Sidebar from "./Sidebar";
import Footer from "./Footer";
import { Toaster } from "@/components/ui/sonner";
import { List, Warning, Buildings, X, Globe } from "@phosphor-icons/react";
import { useAuth, canManage } from "@/contexts/AuthContext";
import { useEmpresa } from "@/contexts/EmpresaContext";
import api from "@/lib/api";

function SubscriptionBanner() {
  const { user } = useAuth();
  const [estado, setEstado] = useState(null);

  useEffect(() => {
    if (!user || !canManage(user)) return;
    api
      .get("/suscripciones/mi-estado")
      .then((r) => setEstado(r.data))
      .catch(() => {});
  }, [user]);

  const vig = estado?.estado_vigencia;
  if (!estado?.tiene_suscripcion || !["periodo_gracia", "suspendida"].includes(vig)) return null;

  const suspendida = vig === "suspendida";
  return (
    <div
      className={`px-4 py-2.5 text-sm flex items-center gap-2 ${
        suspendida ? "bg-red-600 text-white" : "bg-amber-100 text-amber-900 border-b border-amber-200"
      }`}
      data-testid="subscription-banner"
    >
      <Warning size={18} weight="fill" />
      <span className="flex-1">
        {suspendida
          ? "Tu suscripción está vencida. No podrás dar de alta trabajadores, centros ni usuarios hasta regularizar el pago (los registros de asistencia siguen activos)."
          : "Tu suscripción venció y está en periodo de gracia. Regulariza el pago pronto para no perder el acceso de gestión."}
      </span>
      <Link to="/app/suscripcion" className="underline font-semibold whitespace-nowrap">
        Ver suscripción
      </Link>
    </div>
  );
}

// Franja de contexto para el super_admin: deja claro sobre qué empresa está trabajando
// (o si está en la vista de plataforma con todas). Ver EmpresaContext / EmpresaSwitcher.
function EmpresaContextBanner() {
  const { habilitado, empresaActiva, esVistaPlataforma, setEmpresaActivaId } = useEmpresa();
  if (!habilitado) return null;

  if (esVistaPlataforma) {
    return (
      <div
        className="px-4 py-2 text-xs flex items-center gap-2 bg-slate-100 text-slate-500 border-b border-slate-200"
        data-testid="empresa-context-banner"
      >
        <Globe size={14} weight="bold" />
        <span>Vista de plataforma — datos de todas las empresas. Elige una empresa en el menú lateral para acotar.</span>
      </div>
    );
  }

  return (
    <div
      className="px-4 py-2 text-sm flex items-center gap-2 bg-brand text-white"
      data-testid="empresa-context-banner"
    >
      <Buildings size={16} weight="fill" />
      <span className="flex-1">
        Trabajando en <strong className="font-semibold">{empresaActiva?.razon_social}</strong>
      </span>
      <button
        onClick={() => setEmpresaActivaId("")}
        className="flex items-center gap-1 text-xs font-semibold underline decoration-white/40 hover:decoration-white whitespace-nowrap"
        data-testid="empresa-context-clear"
      >
        <X size={12} weight="bold" />
        Ver todas
      </button>
    </div>
  );
}

export default function Layout() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  const { empresaActivaId } = useEmpresa();

  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-white">
      <Sidebar open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
      <div className="lg:ml-64 min-h-screen flex flex-col">
        <header className="lg:hidden sticky top-0 z-20 flex items-center gap-3 px-4 h-14 bg-white border-b border-slate-200">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="p-2 -ml-2 text-slate-700 hover:text-ink"
            aria-label="Abrir menú"
            data-testid="mobile-menu-btn"
          >
            <List size={22} weight="bold" />
          </button>
          <img src="/brand/monograma.png" alt="" className="w-7 h-7 shrink-0" />
          <span className="font-display font-black text-lg text-ink">CHKR</span>
        </header>
        <SubscriptionBanner />
        <EmpresaContextBanner />
        {/* key: al cambiar la empresa activa, se re-montan las páginas para que
            vuelvan a cargar sus datos ya acotados a la empresa elegida. */}
        <main className="flex-1" key={empresaActivaId || "__todas__"}>
          <Outlet />
        </main>
        <Footer className="border-t border-slate-100" />
      </div>
      <Toaster richColors position="top-right" />
    </div>
  );
}
